import React, { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { MenuIcon, XIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Logo } from './ui/Logo';
export const Navbar = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const openCalendly = () => {
    window.open('https://calendly.com/zerototendigital/exploration-feature-design', '_blank');
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // Intersection Observer for section tracking
    const sections = document.querySelectorAll('[id]');
    const navLinks = document.querySelectorAll('[data-nav]');
    const observerOptions = {
      rootMargin: '-40% 0px -50% 0px',
      threshold: 0.01
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
          // Add is-active class to the corresponding nav link
          navLinks.forEach(link => {
            const linkHref = link.getAttribute('data-nav');
            link.classList.toggle('is-active', linkHref === entry.target.id);
          });
        }
      });
    }, observerOptions);
    sections.forEach(section => observer.observe(section));
    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);
  useEffect(() => {
    // Prevent body scroll when mobile menu is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const scrollTo = (id: string) => {
    setIsOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm py-2' : 'bg-transparent py-4'}`} data-id="element-29">
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center" data-id="element-30">
        <div className="flex items-center" data-id="element-31">
          <Logo data-id="element-32" />
        </div>
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8" data-id="element-33">
          <button onClick={() => scrollTo('benefits')} className={`text-text-secondary hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'benefits' ? 'text-primary font-semibold border-b-2 border-primary is-active' : ''}`} data-nav="benefits" data-id="element-34">
            Benefits
          </button>
          <button onClick={() => scrollTo('services')} className={`text-text-secondary hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'services' ? 'text-primary font-semibold border-b-2 border-primary is-active' : ''}`} data-nav="services" data-id="element-35">
            Services
          </button>
          <button onClick={() => scrollTo('pricing')} className={`text-text-secondary hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'pricing' ? 'text-primary font-semibold border-b-2 border-primary is-active' : ''}`} data-nav="pricing" data-id="element-36">
            Pricing
          </button>
          <Button onClick={openCalendly} variant="secondary" className="text-xs px-3 py-1.5 focus:ring-2 focus:ring-primary focus:ring-offset-2" data-id="element-37">
            Speak with a Senior PM
          </Button>
        </div>
        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center" data-id="element-38">
          <Button onClick={openCalendly} variant="secondary" className="mr-3 focus:ring-2 focus:ring-primary focus:ring-offset-2 text-xs px-2 py-1" data-id="element-39">
            Speak with a Senior PM
          </Button>
          <button className="text-black focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 p-1 rounded" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} aria-label="Toggle navigation menu" data-id="element-40">
            {isOpen ? <XIcon size={24} data-id="element-41" /> : <MenuIcon size={24} data-id="element-42" />}
          </button>
        </div>
      </div>
      {/* Mobile Menu */}
      {isOpen && <div className="md:hidden bg-white w-full py-4 px-4 shadow-lg absolute top-full left-0 h-[calc(100vh-80px)] overflow-auto" data-id="element-43">
          <div className="flex flex-col space-y-4" data-id="element-44">
            <button onClick={() => scrollTo('benefits')} className={`text-text-secondary hover:text-black transition-colors py-4 text-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'benefits' ? 'text-primary font-semibold border-l-4 border-primary pl-2 is-active' : ''}`} data-nav="benefits" data-id="element-45">
              Benefits
            </button>
            <button onClick={() => scrollTo('services')} className={`text-text-secondary hover:text-black transition-colors py-4 text-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'services' ? 'text-primary font-semibold border-l-4 border-primary pl-2 is-active' : ''}`} data-nav="services" data-id="element-46">
              Services
            </button>
            <button onClick={() => scrollTo('pricing')} className={`text-text-secondary hover:text-black transition-colors py-4 text-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'pricing' ? 'text-primary font-semibold border-l-4 border-primary pl-2 is-active' : ''}`} data-nav="pricing" data-id="element-47">
              Pricing
            </button>
            <div className="pt-4 mt-4 border-t border-gray-100" data-id="element-48">
              <Button onClick={openCalendly} variant="secondary" className="w-full focus:ring-2 focus:ring-primary focus:ring-offset-2 text-sm py-2" data-id="element-49">
                Speak with a Senior PM
              </Button>
            </div>
          </div>
        </div>}
      {/* Scroll Progress Indicator (Desktop Only) */}
      <div className="hidden lg:block fixed right-6 top-1/2 transform -translate-y-1/2 z-40" data-id="element-50">
        <div className="flex flex-col items-center gap-4" data-id="element-51">
          <button onClick={() => scrollTo('hero')} className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'hero' ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label="Scroll to Hero section" data-id="element-52"></button>
          <button onClick={() => scrollTo('benefits')} className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'benefits' ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label="Scroll to Benefits section" data-id="element-53"></button>
          <button onClick={() => scrollTo('services')} className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'services' ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label="Scroll to Services section" data-id="element-54"></button>
          <button onClick={() => scrollTo('testimonials')} className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'testimonials' ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label="Scroll to Testimonials section" data-id="element-55"></button>
          <button onClick={() => scrollTo('pricing')} className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'pricing' ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label="Scroll to Pricing section" data-id="element-56"></button>
          <button onClick={() => scrollTo('faq')} className={`w-3 h-3 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${activeSection === 'faq' ? 'bg-primary' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label="Scroll to FAQ section" data-id="element-57"></button>
        </div>
      </div>
    </nav>;
};